/**
 * The money rules: how an amount is split, what one transaction makes people
 * owe each other, and how a group's debts net down.
 *
 * Everything here is integer cents in and integer cents out. There is no
 * float, no `toFixed`, and no rounding at the call site: a number that leaves
 * this module is exactly what belongs in an `*_cents` column. Rounding happens
 * in exactly one place — `splitCents` — and it is largest-remainder with an
 * explicit tie-break, so the same expense splits the same way on every device
 * rather than however that device's iteration order happened to fall.
 *
 * The shapes mirror `migrations/0002_core.sql`: a `Line` is a row of
 * `transaction_lines`, an `Edge` is a row of `ledger_edges`.
 */

export type UserId = string

/** Which side of a transaction a line is on, as in `transaction_lines.side`. */
export type Side = 'paid' | 'owes'

/** One row of `transaction_lines`, for a single transaction. */
export interface Line {
  userId: UserId
  side: Side
  amountCents: number
}

/** One row of `ledger_edges`: `debtorId` owes `creditorId` `amountCents`. */
export interface Edge {
  debtorId: UserId
  creditorId: UserId
  amountCents: number
}

/** A person's net position. Positive: they are owed. Negative: they owe. */
export interface Balance {
  userId: UserId
  amountCents: number
}

/**
 * Compares ids by code unit, not `localeCompare`: collation is locale- and
 * ICU-version-dependent, and every order in this module has to be the same
 * order on every device.
 */
function compareIds(a: UserId, b: UserId): number {
  return a < b ? -1 : a > b ? 1 : 0
}

function assertCents(value: number, what: string): void {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`${what} must be a whole number of cents, got ${value}`)
  }
}

function assertNonNegativeCents(value: number, what: string): void {
  assertCents(value, what)
  if (value < 0) throw new RangeError(`${what} must not be negative, got ${value}`)
}

/**
 * Exact integer division. `Math.floor(a / b)` goes through a float divide,
 * which can land a unit either side of the true quotient near the top of the
 * safe-integer range. The drift is never more than one, so correcting it costs
 * two comparisons and the result is exact for every input we accept.
 */
function floorDiv(numerator: number, denominator: number): number {
  let q = Math.floor(numerator / denominator)
  while (q * denominator > numerator) q -= 1
  while ((q + 1) * denominator <= numerator) q += 1
  return q
}

/**
 * Splits `totalCents` in proportion to `weights`, by the largest-remainder
 * method: everyone gets their exact share rounded down, then the cents left
 * over go one each to the largest remainders.
 *
 * The result always sums to `totalCents` exactly, and every share is the exact
 * fair share rounded either down or up — never further out than that.
 *
 * **A tie goes to the earlier entry.** Two people owed the same fraction of a
 * cent are separated by their position in `weights` and by nothing else, so
 * the caller decides, deterministically, by the order it passes members in.
 * Pass them in an order the database gives you (`user_id`, or `joined_at`),
 * never in whatever order a `Map` or an object's keys came out.
 */
export function splitCents(totalCents: number, weights: readonly number[]): number[] {
  assertNonNegativeCents(totalCents, 'totalCents')

  let totalWeight = 0
  for (const weight of weights) {
    assertNonNegativeCents(weight, 'weight')
    totalWeight += weight
  }

  if (totalWeight === 0) {
    // Nobody to give it to. Splitting nothing between nobody is fine; splitting
    // real money between nobody would lose it, which is the one thing this
    // module exists to prevent.
    if (totalCents !== 0) {
      throw new RangeError(`cannot split ${totalCents} cents between zero total weight`)
    }
    return weights.map(() => 0)
  }

  const shares = weights.map((weight) => {
    const product = totalCents * weight
    assertCents(product, 'totalCents * weight')
    const base = floorDiv(product, totalWeight)
    return { base, remainder: product - base * totalWeight }
  })

  let remaining = totalCents - shares.reduce((sum, share) => sum + share.base, 0)

  const order = shares.map((_, index) => index)
  order.sort((a, b) => shares[b].remainder - shares[a].remainder || a - b)
  for (const index of order) {
    if (remaining === 0) break
    shares[index].base += 1
    remaining -= 1
  }

  return shares.map((share) => share.base)
}

/**
 * Splits `totalCents` evenly `parts` ways. The odd cents go to the first
 * entries: `equalSplit(6001, 4)` is `[1501, 1500, 1500, 1500]`, which is why
 * the fixture's gas transaction puts the odd cent on Ada.
 */
export function equalSplit(totalCents: number, parts: number): number[] {
  assertNonNegativeCents(parts, 'parts')
  return splitCents(totalCents, new Array<number>(parts).fill(1))
}

// ---------------------------------------------------------------------------
// Splits: turning what somebody typed into what everybody owes
// ---------------------------------------------------------------------------

/**
 * How a total is divided between the people it is split between. The client
 * picks the mode and supplies its inputs; resolving those to cents is this
 * module's job and nowhere else's, so the same expense comes out the same
 * whether it was entered on a phone, replayed from an offline queue, or
 * corrected by an edit a week later.
 */
export type SplitMode = 'equal' | 'exact' | 'percentage' | 'shares' | 'adjustment'

export const SPLIT_MODES: readonly SplitMode[] = [
  'equal',
  'exact',
  'percentage',
  'shares',
  'adjustment',
]

/**
 * One person's slice of one transaction: the cents they paid, or the cents
 * they owe. Never negative — direction is carried by which list it is in, the
 * way `transaction_lines.side` carries it in the database.
 */
export interface Allocation {
  userId: UserId
  amountCents: number
}

/** One participant's input to a split, in whichever unit `mode` reads. */
export interface SplitParticipant {
  userId: UserId
  /** `exact`: the cents this person owes. */
  amountCents?: number
  /** `percentage`: basis points — 12.5% is 1250, the whole bill is 10000. */
  percentBips?: number
  /** `shares`: how many shares of the bill this person carries. */
  shares?: number
  /**
   * `adjustment`: cents that are *this person's alone* before the rest is
   * divided equally. The one signed number in the whole module — somebody who
   * skipped the wine is a negative adjustment, and a mode that could only add
   * would be half a mode.
   */
  adjustmentCents?: number
}

export interface Split {
  mode: SplitMode
  /**
   * In the order the split should break ties in — `splitCents` gives an odd
   * cent to the earliest entry, so this has to be an order the database
   * produced (`joined_at`, then `user_id`) rather than the order a request
   * body happened to list people in.
   */
  participants: readonly SplitParticipant[]
}

/** A whole bill in basis points. Percentages are integers in here, never floats. */
export const PERCENT_BIPS = 10_000

/**
 * A percentage as a person writes it (`12.5`) to the integer basis points the
 * split runs on (`1250`).
 *
 * This is the one place a non-integer number is allowed anywhere near money,
 * and it is allowed only to be turned into an integer immediately: a
 * percentage is an input, not an amount. Anything finer than a hundredth of a
 * percent is refused rather than rounded, because rounding an input silently
 * is how a bill ends up not adding to 100%.
 */
export function percentToBips(percent: number): number {
  if (!Number.isFinite(percent)) {
    throw new RangeError(`percent must be a finite number, got ${percent}`)
  }
  if (percent < 0) throw new RangeError(`percent must not be negative, got ${percent}`)
  const bips = Math.round(percent * PERCENT_BIPS / 100)
  if (Math.abs(percent * PERCENT_BIPS / 100 - bips) > 1e-6) {
    throw new RangeError(`percent ${percent} is finer than a hundredth of a percent`)
  }
  return bips
}

/**
 * Resolves a split to the exact number of cents each participant owes.
 *
 * The result has one entry per participant, in the order they were passed,
 * including anybody who comes out at zero — a caller writing
 * `transaction_lines` drops those (the column is `CHECK (amount_cents > 0)`),
 * and a caller drawing a screen wants to show them.
 *
 * Whatever the mode, the entries sum to `totalCents` exactly. `exact` and
 * `percentage` are checked rather than rounded into shape: a client that sends
 * amounts adding to $99.99 of a $100 bill, or percentages adding to 99%, has a
 * bug, and quietly absorbing the difference would hide it.
 */
export function resolveSplit(totalCents: number, split: Split): Allocation[] {
  assertNonNegativeCents(totalCents, 'totalCents')

  const { mode, participants } = split
  if (participants.length === 0) {
    throw new RangeError('a split needs at least one participant')
  }
  const seen = new Set<UserId>()
  for (const participant of participants) {
    if (seen.has(participant.userId)) {
      throw new RangeError(`${participant.userId} appears twice in the split`)
    }
    seen.add(participant.userId)
  }

  const paired = (amounts: readonly number[]): Allocation[] =>
    participants.map((participant, index) => ({
      userId: participant.userId,
      amountCents: amounts[index],
    }))

  const required = (
    name: 'amountCents' | 'percentBips' | 'shares' | 'adjustmentCents',
    participant: SplitParticipant,
  ): number => {
    const value = participant[name]
    if (value === undefined) {
      throw new RangeError(`${mode} split needs ${name} for ${participant.userId}`)
    }
    return value
  }

  const field = (
    name: 'amountCents' | 'percentBips' | 'shares',
    participant: SplitParticipant,
  ): number => {
    const value = required(name, participant)
    assertNonNegativeCents(value, `${name} for ${participant.userId}`)
    return value
  }

  const total = (values: readonly number[]) => values.reduce((sum, value) => sum + value, 0)

  switch (mode) {
    case 'equal':
      return paired(equalSplit(totalCents, participants.length))

    case 'exact': {
      const amounts = participants.map((participant) => field('amountCents', participant))
      const given = total(amounts)
      if (given !== totalCents) {
        throw new RangeError(`exact split comes to ${given} cents, not ${totalCents}`)
      }
      return paired(amounts)
    }

    case 'percentage': {
      const bips = participants.map((participant) => field('percentBips', participant))
      const given = total(bips)
      if (given !== PERCENT_BIPS) {
        throw new RangeError(`percentages come to ${given} basis points, not ${PERCENT_BIPS}`)
      }
      return paired(splitCents(totalCents, bips))
    }

    case 'shares': {
      const shares = participants.map((participant) => field('shares', participant))
      if (total(shares) === 0) {
        throw new RangeError('a shares split needs somebody to have at least one share')
      }
      return paired(splitCents(totalCents, shares))
    }

    /**
     * Somebody's own share of the bill, taken off the top, and the rest split
     * equally: Ben's $12 cocktail comes out of a $90 dinner, the remaining $78
     * is divided four ways, and Ben's $19.50 has his $12 added back.
     *
     * It is the only mode whose inputs are signed — an adjustment is a
     * correction, and a correction that could only ever be upwards would not
     * let anybody say "I skipped the wine". Both refusals below are the same
     * refusal in two places: an adjustment may move who carries what, and it
     * may never conjure or destroy money, so the amounts still have to be a
     * partition of `totalCents` into non-negative parts.
     */
    case 'adjustment': {
      const adjustments = participants.map((participant) => {
        const value = required('adjustmentCents', participant)
        assertCents(value, `adjustmentCents for ${participant.userId}`)
        return value
      })

      const adjusted = total(adjustments)
      const shared = totalCents - adjusted
      if (shared < 0) {
        throw new RangeError(
          `adjustments come to ${adjusted} cents, more than the ${totalCents} being split`,
        )
      }

      const equal = equalSplit(shared, participants.length)
      const amounts = equal.map((share, index) => share + adjustments[index])

      const below = amounts.findIndex((amount) => amount < 0)
      if (below !== -1) {
        throw new RangeError(
          `${participants[below].userId} would owe ${amounts[below]} cents; ` +
            'an adjustment cannot take somebody below nothing',
        )
      }
      return paired(amounts)
    }

    default:
      throw new RangeError(`unknown split mode ${String(mode)}`)
  }
}

/**
 * The `transaction_lines` rows for one transaction, and the invariant
 * `migrations/0002_core.sql` states enforced in the one place every write goes
 * through: `SUM(paid) = SUM(owes) = transactions.amount_cents`.
 *
 * Zero-cent allocations are dropped rather than written — the column is
 * `CHECK (amount_cents > 0)`, and a line saying somebody owes nothing is not a
 * fact worth a row. Dropping them cannot break the invariant, since a zero
 * contributes nothing to either sum.
 */
export function linesFrom(
  totalCents: number,
  paid: readonly Allocation[],
  owed: readonly Allocation[],
): Line[] {
  assertNonNegativeCents(totalCents, 'totalCents')

  const lines: Line[] = []
  const side = (allocations: readonly Allocation[], sideName: Side): number => {
    const seen = new Set<UserId>()
    let sum = 0
    for (const allocation of allocations) {
      if (seen.has(allocation.userId)) {
        throw new RangeError(`${allocation.userId} has two ${sideName} allocations`)
      }
      seen.add(allocation.userId)
      assertNonNegativeCents(allocation.amountCents, `${sideName} amountCents for ${allocation.userId}`)
      sum += allocation.amountCents
      if (allocation.amountCents > 0) {
        lines.push({ userId: allocation.userId, side: sideName, amountCents: allocation.amountCents })
      }
    }
    return sum
  }

  const paidTotal = side(paid, 'paid')
  const owedTotal = side(owed, 'owes')

  if (paidTotal !== totalCents || owedTotal !== totalCents) {
    throw new RangeError(
      `transaction does not balance: paid ${paidTotal}, owed ${owedTotal}, amount ${totalCents}`,
    )
  }

  return lines
}

function tally(entries: Iterable<readonly [UserId, number]>): Balance[] {
  const net = new Map<UserId, number>()
  for (const [userId, amountCents] of entries) {
    net.set(userId, (net.get(userId) ?? 0) + amountCents)
  }
  return [...net]
    .map(([userId, amountCents]) => ({ userId, amountCents }))
    .sort((a, b) => compareIds(a.userId, b.userId))
}

/**
 * Each person's net position across `lines`: what they paid less what they
 * owe. Positive means the group owes them.
 *
 * Everybody who appears in `lines` gets an entry, including anyone who comes
 * out at exactly zero — a screen wants to say "settled up" rather than not
 * mention them at all. Over a whole group the entries sum to zero.
 */
export function netBalances(lines: readonly Line[]): Balance[] {
  return tally(
    lines.map((line): readonly [UserId, number] => {
      assertNonNegativeCents(line.amountCents, `amountCents for ${line.userId}`)
      if (line.side !== 'paid' && line.side !== 'owes') {
        throw new RangeError(`line side must be 'paid' or 'owes', got ${String(line.side)}`)
      }
      return [line.userId, line.side === 'paid' ? line.amountCents : -line.amountCents]
    }),
  )
}

/** Each person's net position across `edges`, same sign convention as `netBalances`. */
export function balancesFromEdges(edges: readonly Edge[]): Balance[] {
  const entries: (readonly [UserId, number])[] = []
  for (const edge of edges) {
    assertNonNegativeCents(edge.amountCents, `amountCents for ${edge.debtorId} to ${edge.creditorId}`)
    if (edge.debtorId === edge.creditorId) {
      throw new RangeError(`an edge cannot run from ${edge.debtorId} to themselves`)
    }
    entries.push([edge.debtorId, -edge.amountCents], [edge.creditorId, edge.amountCents])
  }
  return tally(entries)
}

/**
 * Turns net positions into who pays whom: largest debt against largest credit,
 * repeatedly, until everyone is at zero. Ties on amount are broken by user id,
 * so the same balances always produce the same edges.
 *
 * Every step transfers `min(debt, credit)`, so every amount is a whole cent
 * already — nothing is rounded here and the edges total the credit exactly.
 * Each step also closes out at least one person, so a pair can never come up
 * twice and the result respects the `ledger_edges` primary key.
 */
function settle(balances: readonly Balance[]): Edge[] {
  const total = balances.reduce((sum, balance) => sum + balance.amountCents, 0)
  if (total !== 0) throw new RangeError(`balances must net to zero, these net to ${total}`)

  const side = (sign: number) =>
    balances
      .filter((balance) => Math.sign(balance.amountCents) === sign)
      .map((balance) => ({ userId: balance.userId, remaining: Math.abs(balance.amountCents) }))
      .sort((a, b) => b.remaining - a.remaining || compareIds(a.userId, b.userId))

  const debtors = side(-1)
  const creditors = side(1)

  const edges: Edge[] = []
  let d = 0
  let c = 0
  while (d < debtors.length && c < creditors.length) {
    const debtor = debtors[d]
    const creditor = creditors[c]
    const amountCents = Math.min(debtor.remaining, creditor.remaining)
    edges.push({ debtorId: debtor.userId, creditorId: creditor.userId, amountCents })
    debtor.remaining -= amountCents
    creditor.remaining -= amountCents
    if (debtor.remaining === 0) d += 1
    if (creditor.remaining === 0) c += 1
  }

  return sortEdges(edges)
}

function sortEdges(edges: Edge[]): Edge[] {
  return edges.sort(
    (a, b) => compareIds(a.debtorId, b.debtorId) || compareIds(a.creditorId, b.creditorId),
  )
}

/**
 * The `ledger_edges` one transaction contributes: who it leaves owing whom.
 *
 * The lines must balance — `SUM(paid) = SUM(owes) = transactions.amount_cents`,
 * the invariant `migrations/0002_core.sql` describes — and the edges returned
 * total that transaction's credit, the amount its payers are out of pocket.
 */
export function edgesFor(lines: readonly Line[]): Edge[] {
  let paid = 0
  let owed = 0
  for (const line of lines) {
    assertNonNegativeCents(line.amountCents, `amountCents for ${line.userId}`)
    if (line.side === 'paid') paid += line.amountCents
    else owed += line.amountCents
  }
  if (paid !== owed) {
    throw new RangeError(`transaction does not balance: paid ${paid}, owed ${owed}`)
  }
  return settle(netBalances(lines))
}

/**
 * Nets `edges` down to at most one row per pair of people, oriented so the
 * amount is positive — the shape `ledger_edges` stores. A pair that has
 * settled drops out rather than appearing as a zero.
 */
export function pairwise(edges: readonly Edge[]): Edge[] {
  // Keyed on the pair with the lower id first, so A owes B and B owes A land on
  // the same entry; positive means the lower id owes the higher one. The key is
  // only ever looked up, never parsed, so an id containing the separator is
  // still unambiguous.
  const net = new Map<string, { low: UserId; high: UserId; amountCents: number }>()
  for (const edge of edges) {
    assertNonNegativeCents(edge.amountCents, `amountCents for ${edge.debtorId} to ${edge.creditorId}`)
    if (edge.debtorId === edge.creditorId) {
      throw new RangeError(`an edge cannot run from ${edge.debtorId} to themselves`)
    }
    const forwards = compareIds(edge.debtorId, edge.creditorId) < 0
    const low = forwards ? edge.debtorId : edge.creditorId
    const high = forwards ? edge.creditorId : edge.debtorId
    const key = `${low} ${high}`
    const entry = net.get(key) ?? { low, high, amountCents: 0 }
    entry.amountCents += forwards ? edge.amountCents : -edge.amountCents
    net.set(key, entry)
  }

  const result: Edge[] = []
  for (const { low, high, amountCents } of net.values()) {
    if (amountCents === 0) continue
    result.push(
      amountCents > 0
        ? { debtorId: low, creditorId: high, amountCents }
        : { debtorId: high, creditorId: low, amountCents: -amountCents },
    )
  }
  return sortEdges(result)
}

/**
 * Rewrites `edges` into the fewest payments that leave everyone in the same
 * net position. It changes who pays whom — a cycle where A owes B owes C owes
 * A just disappears — and changes nobody's balance by a cent.
 *
 * With `n` people who are not already settled the result is at most `n - 1`
 * edges. It depends only on the balances, so it is idempotent and unaffected
 * by the order `edges` arrives in.
 */
export function simplify(edges: readonly Edge[]): Edge[] {
  return settle(balancesFromEdges(edges))
}
